/**
 * One-click cart upsells, rendered by snippets/cart-upsell.liquid under a cart line, and by
 * snippets/cart-companion-offer.liquid under the cart items:
 *
 * - Upgrade (data-target-variant): swaps a single 60-capsule bottle for the 180-capsule pack. The
 *   180 pack is added first (keeping the line's subscription plan, if any) and only then is the
 *   single bottle removed, so a failed add never leaves the customer with an emptier cart.
 * - Best deal (data-new-quantity): sets the 180-pack line to 2 packs, same plan. The free bottles
 *   are then added by assets/free-bottle-offer.js, which listens for the cart update.
 * - Companion (data-add-variant): adds the companion product. Its discount is an automatic one,
 *   so it comes off in the cart on its own.
 *
 * The cart drawer / cart page is re-rendered through the theme's own `cart:update` event.
 * Uses event delegation because the cart markup is re-rendered (morphed) on every cart change.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const EVENT_SOURCE = 'cart-upsell';

  const request = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `${path} responded ${response.status}`);
      }
      return data;
    });

  const cartSectionIds = () => [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId)
    ),
  ];

  const changeLine = (lineKey, quantity) =>
    request('cart/change.js', {
      id: lineKey,
      quantity,
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    });

  const refreshCart = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: EVENT_SOURCE,
          data: {
            source: EVENT_SOURCE,
            itemCount: cart ? cart.item_count : undefined,
            sections: cart ? cart.sections : undefined,
          },
        },
      })
    );
  };

  const upgrade = async (button) => {
    const card = button.closest('[data-cart-upsell-card], [data-companion-offer]');
    const error = card ? card.querySelector('.cart-upsell__error') : null;
    const { lineKey, targetVariant, sellingPlan, newQuantity, addVariant } = button.dataset;
    // Kept as markup: the glowing button wraps its label in a span.
    const label = button.innerHTML;
    let added = false;

    button.disabled = true;
    button.textContent = 'Adding...';
    if (error) error.hidden = true;

    try {
      let cart;

      if (addVariant) {
        const response = await request('cart/add.js', {
          items: [{ id: Number(addVariant), quantity: 1 }],
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        });
        // add.js returns the added line, not the whole cart: read the cart for the item count.
        const state = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        cart = { ...state, sections: response.sections };
      } else if (newQuantity) {
        cart = await changeLine(lineKey, Number(newQuantity));
      } else {
        const item = { id: Number(targetVariant), quantity: 1 };
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        // Add the 180 pack first: if that fails, the single bottle is still in the cart.
        await request('cart/add.js', { items: [item] });
        added = true;

        cart = await changeLine(lineKey, 0);
      }

      refreshCart(cart);
    } catch (err) {
      console.error('[cart-upsell]', err);

      if (added) {
        // The 180 pack went in but the single bottle couldn't be removed: show the real cart.
        refreshCart(null);
        return;
      }

      button.disabled = false;
      button.innerHTML = label;
      if (error) {
        error.textContent = "Sorry, we couldn't update your cart just now. Please try again.";
        error.hidden = false;
      }
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-upsell]') : null;
    if (!button || button.disabled) return;

    event.preventDefault();
    upgrade(button);
  });
})();
